import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import StarRating from "../components/StarRating";
import { Wrench, MessageSquareQuote, ArrowLeft } from "lucide-react";

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("pt-PT", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function PublicReviewsPage() {
    const [reviews, setReviews] = useState(null);
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        Promise.all([
            axios.get(`${API}/public/reviews`),
            axios.get(`${API}/public/settings`).catch(() => ({ data: null })),
        ]).then(([r, s]) => {
            setReviews(r.data || []);
            setSettings(s.data);
        });
    }, []);

    const primaryColor =
        settings?.cor_primaria && /^#([0-9A-Fa-f]{6})$/.test(settings.cor_primaria)
            ? settings.cor_primaria
            : "#FF3B30";
    const oficinaNome = settings?.nome || "Oficina Moto";

    const avgRating =
        reviews && reviews.length > 0
            ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length
            : 0;

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="public-reviews-page">
            <header className="border-b border-zinc-200 bg-white">
                <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-4">
                    {settings?.logo_url ? (
                        <img
                            src={settings.logo_url}
                            alt={oficinaNome}
                            className="h-10 w-10 rounded-sm border border-zinc-200 object-cover"
                        />
                    ) : (
                        <div
                            className="flex h-9 w-9 items-center justify-center rounded-sm text-white"
                            style={{ backgroundColor: primaryColor }}
                        >
                            <Wrench className="h-4 w-4" />
                        </div>
                    )}
                    <div>
                        <div className="font-['Outfit'] text-base font-semibold tracking-tight text-zinc-900">
                            {oficinaNome}
                        </div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
                            Opiniões de clientes
                        </div>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-6 py-10">
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-zinc-500">
                    Feedback verificado
                </div>
                <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-zinc-900 md:text-4xl">
                    O que dizem os motociclistas sobre nós.
                </h1>

                {reviews && reviews.length > 0 ? (
                    <div className="mt-6 flex items-center gap-4 border border-zinc-200 bg-white p-5">
                        <div>
                            <div className="font-['Outfit'] text-3xl font-semibold text-zinc-900">
                                {avgRating.toFixed(1)}
                            </div>
                            <div className="text-xs text-zinc-500">
                                {reviews.length} avalia{reviews.length === 1 ? "ção" : "ções"}
                            </div>
                        </div>
                        <div className="border-l border-zinc-200 pl-4">
                            <StarRating value={Math.round(avgRating)} readOnly color={primaryColor} testid="avg-rating" />
                        </div>
                    </div>
                ) : null}

                <div className="mt-8 space-y-4">
                    {reviews === null ? (
                        <div className="flex items-center justify-center py-16">
                            <div
                                className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-300"
                                style={{ borderTopColor: primaryColor }}
                            />
                        </div>
                    ) : reviews.length === 0 ? (
                        <div className="border border-zinc-200 bg-white p-10 text-center" data-testid="reviews-empty">
                            <MessageSquareQuote className="mx-auto mb-3 h-8 w-8 text-zinc-400" />
                            <h3 className="font-['Outfit'] text-lg font-semibold text-zinc-900">
                                Ainda sem avaliações públicas.
                            </h3>
                            <p className="mt-1 text-sm text-zinc-600">
                                Seja o primeiro a partilhar a sua experiência após o próximo serviço.
                            </p>
                        </div>
                    ) : (
                        reviews.map((r) => (
                            <article
                                key={r.id}
                                className="border border-zinc-200 bg-white p-6"
                                data-testid={`public-review-${r.id}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="font-medium text-zinc-900">
                                            {r.cliente_primeiro_nome}
                                        </div>
                                        <div className="text-xs text-zinc-500">
                                            {r.moto_label} · {formatDate(r.reviewed_at || r.created_at)}
                                        </div>
                                    </div>
                                    <StarRating
                                        value={r.rating}
                                        readOnly
                                        size={18}
                                        color={primaryColor}
                                        testid={`public-review-stars-${r.id}`}
                                    />
                                </div>
                                {r.comment ? (
                                    <p className="mt-3 text-sm leading-relaxed text-zinc-700">{r.comment}</p>
                                ) : null}
                            </article>
                        ))
                    )}
                </div>

                <div className="mt-10 text-center text-xs text-zinc-500">
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-1 font-semibold hover:underline"
                        style={{ color: primaryColor }}
                    >
                        <ArrowLeft className="h-3 w-3" />
                        Voltar
                    </Link>
                </div>
            </main>
        </div>
    );
}
